import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { addTranslations } from './locales/translations';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SnackbarProvider } from 'notistack';
import Navbar from './components/Navbar';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import PwaInstallBanner from './components/PwaInstallBanner';
import UmamiTracker from './components/UmamiTracker';
import BugReportButton from './components/BugReportButton';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import Dashboard from './pages/Dashboard';
import CreateTask from './pages/tasks/CreateTask';
import TaskList from './pages/tasks/TaskList';
import TaskDetails from './pages/tasks/TaskDetails';
import MyTasks from './pages/tasks/MyTasks';
import CreateBid from './pages/bids/CreateBid';
import MyBids from './pages/bids/MyBids';
import ChatPage from './pages/messages/ChatPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ProfessionalProfile from './pages/profile/ProfessionalProfile';
import ClientProfile from './pages/profile/ClientProfile';
import AdminOverview from './pages/admin/AdminOverview';
import ProfessionalAgenda from './pages/professional/ProfessionalAgenda';
import Terms from './pages/legal/Terms';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

const queryClient = new QueryClient();

// Добавяне на новите преводи
addTranslations(i18n);

// Защитен маршрут компонент
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Зареждане...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Защитен маршрут за клиенти
const ClientRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Зареждане...</div>;
  }

  if (!isAuthenticated || user?.user_type !== 'client') {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Защитен маршрут за професионалисти
const ProfessionalRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Зареждане...</div>;
  }

  if (!isAuthenticated || user?.user_type !== 'professional') {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Защитен маршрут за администратори (superuser)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Зареждане...</div>;
  }

  if (!isAuthenticated || !user?.is_superuser) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <Provider store={store}>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <WebSocketProvider>
              <QueryClientProvider client={queryClient}>
                <ThemeProvider>
                  <SnackbarProvider maxSnack={3}>
                    <div className="min-h-screen bg-gray-50">
                      <UmamiTracker />
                      <BugReportButton />
                      <Navbar />
                      <EmailVerificationBanner />
                      <PwaInstallBanner />
                      <Routes>
                        {/* Публични маршрути */}
                        <Route path="/" element={<Home />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />

                        {/* Защитени маршрути */}
                        <Route path="/dashboard" element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        } />

                        {/* Маршрути за клиенти */}
                        <Route path="/profile/client/:id/dashboard" element={
                          <ClientRoute>
                            <Dashboard />
                          </ClientRoute>
                        } />
                        <Route path="/tasks/create" element={
                          <ClientRoute>
                            <CreateTask />
                          </ClientRoute>
                        } />
                        <Route path="/tasks/my" element={
                          <ClientRoute>
                            <MyTasks />
                          </ClientRoute>
                        } />

                        {/* Маршрути за професионалисти */}
                        <Route path="/tasks" element={
                          <ProfessionalRoute>
                            <TaskList />
                          </ProfessionalRoute>
                        } />
                        <Route path="/tasks/:id" element={
                          <ProtectedRoute>
                            <TaskDetails />
                          </ProtectedRoute>
                        } />
                        <Route path="/bids/create/:taskId" element={
                          <ProfessionalRoute>
                            <CreateBid />
                          </ProfessionalRoute>
                        } />
                        <Route path="/bids/my" element={
                          <ProfessionalRoute>
                            <MyBids />
                          </ProfessionalRoute>
                        } />
                        <Route path="/profile/professional/:id/dashboard" element={
                          <ProfessionalRoute>
                            <Dashboard />
                          </ProfessionalRoute>
                        } />
                        <Route path="/profile/professional/:id" element={
                          <ProtectedRoute>
                            <ProfessionalProfile />
                          </ProtectedRoute>
                        } />
                        <Route path="/profile/professional/:id/agenda" element={
                          <ProfessionalRoute>
                            <ProfessionalAgenda />
                          </ProfessionalRoute>
                        } />
                        <Route path="/profile/client/:id" element={
                          <ProtectedRoute>
                            <ClientProfile />
                          </ProtectedRoute>
                        } />
                        <Route path="/messages" element={
                          <ProtectedRoute>
                            <ChatPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/profile/client/:id/messages" element={
                          <ClientRoute>
                            <ChatPage />
                          </ClientRoute>
                        } />
                        <Route path="/profile/professional/:id/messages" element={
                          <ProfessionalRoute>
                            <ChatPage />
                          </ProfessionalRoute>
                        } />
                        <Route path="/messages/:userId" element={
                          <ProtectedRoute>
                            <ChatPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/admin" element={
                          <AdminRoute>
                            <AdminOverview />
                          </AdminRoute>
                        } />
                      </Routes>
                    </div>
                  </SnackbarProvider>
                </ThemeProvider>
              </QueryClientProvider>
            </WebSocketProvider>
          </AuthProvider>
        </Router>
      </Provider>
    </I18nextProvider>
  );
}

export default App;
